/**
 * Unified ordering for chat transcript rows (user bubbles + agent bubbles).
 *
 * Pure `clientOrder` sorting breaks when Redis merge / multi-task sessions assign
 * monotonic orders that do not match wall-clock order across turns (e.g. a later
 * user message can get a lower sequence than agent rows from an earlier turn).
 *
 * Strategy: if timestamps differ by more than a few minutes, trust chronology;
 * otherwise preserve stream order (`clientOrder`) to handle same-turn replies and
 * small client/server clock skew.
 */
export const CHAT_DISPLAY_ORDER_TIME_GAP_MS = 120_000; // 2 minutes
/**
 * When `clientOrder` disagrees with chronology and the wall-clock gap exceeds this
 * window, trust the timestamp. This keeps an agent reply from floating above the user
 * question that prompted it when Redis-merge assigns the reply a lower sequence than the
 * next user row. Gaps below this threshold are treated as same-turn and keep stream order.
 */
export const CHAT_DISPLAY_ORDER_PIN_USER_AFTER_MS = 5_000; // 5 seconds

export function compareChatDisplayOrder(
  a: { clientOrder: number; sortTimeMs: number },
  b: { clientOrder: number; sortTimeMs: number },
): number {
  const aTime = Number.isFinite(a.sortTimeMs) ? a.sortTimeMs : 0;
  const bTime = Number.isFinite(b.sortTimeMs) ? b.sortTimeMs : 0;
  const timeDiff = aTime - bTime;
  if (Math.abs(timeDiff) > CHAT_DISPLAY_ORDER_TIME_GAP_MS) {
    return timeDiff;
  }
  // Treat clientOrder as meaningful only when both rows carry a real (finite, positive)
  // sequence. Missing/zero orders fall back to chronology.
  const aOrder = Number.isFinite(a.clientOrder) && a.clientOrder > 0 ? a.clientOrder : 0;
  const bOrder = Number.isFinite(b.clientOrder) && b.clientOrder > 0 ? b.clientOrder : 0;
  const orderDiff = aOrder - bOrder;
  if (orderDiff !== 0) {
    // Within the time-gap window, stream order is the source of truth. User bubbles are
    // stamped with the client clock while agent bubbles carry server time, so a client
    // running a few seconds ahead/behind the server must NOT push a reply above the
    // question that prompted it.
    //
    // Exception: when clientOrder disagrees with chronology by more than
    // CHAT_DISPLAY_ORDER_PIN_USER_AFTER_MS (typically because a reply's WS frame arrived
    // after the user already sent the next question, or Redis-merge produced a mixed
    // counter space), trust the timestamp for that pair.
    if (Math.abs(timeDiff) > CHAT_DISPLAY_ORDER_PIN_USER_AFTER_MS) {
      return timeDiff;
    }
    return orderDiff;
  }
  if (timeDiff !== 0) return timeDiff;
  return 0;
}
